/** Compact persistent ticker header for the Ticker Intelligence Workspace.
 * Mockup only. Every value comes from a fixture.
 */
import { StatusBadge } from "@/components/wheel/StatusBadge";
import {
  DecisionBadge,
  DeltaText,
  EarningsQualityBadge,
  OptionStateBadge,
  ScoreBar,
  useWorkspaceAdmin,
} from "@/workspace/primitives";
import { useWorkspaceSelection } from "@/workspace/selection";
import { LIVE_UNAVAILABLE } from "@/workspace/live/workspaceLiveUnavailable";

function Cell({ label, children, wide }) {
  return (
    <div className={`min-w-0 ${wide ? "col-span-2" : ""}`}>
      <div className="text-[9px] uppercase tracking-wide truncate" style={{ color: "var(--hoip-text-tertiary)" }}>
        {label}
      </div>
      <div className="mt-0.5 min-w-0">{children}</div>
    </div>
  );
}

const QUOTE_QUALITY = {
  observed: { tier: "info", text: "Observed quote" },
  cached: { tier: "info", text: "Cached data" },
  partial: { tier: "blocking", text: "Required quote fields unavailable" },
  unverified: { tier: "blocking", text: "Option quote timestamp unavailable" },
  modelled: { tier: "caution", text: "Indicative estimate only" },
  unavailable: { tier: "blocking", text: "Data unavailable" },
};

export function TickerHeader({ t }) {
  const isAdmin = useWorkspaceAdmin();
  const { decision } = useWorkspaceSelection();
  const q = QUOTE_QUALITY[t.quoteQuality] || QUOTE_QUALITY.observed;
  const instantState = t.regime.instantState;
  const confirmedState = t.regime.confirmedState;
  const canCompare = Boolean(instantState && confirmedState);
  const confirmedDiffers = canCompare && confirmedState !== instantState;

  return (
    <header
      className="border p-3 min-w-0"
      style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}
      data-testid="ticker-header"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h1 className="text-xl font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>{t.ticker}</h1>
            {t.companyName ? (
              <span className="text-[12px] truncate" style={{ color: "var(--hoip-text-secondary)" }}>
                {t.companyName}
              </span>
            ) : null}
          </div>
          <div className="text-[10px] mt-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>
            {[t.sector, t.industry].filter(Boolean).join(" · ") || "Sector unavailable"}
          </div>
        </div>
        <div className="min-w-0 text-right">
          <DecisionBadge
            state={decision.state}
            text={decision.headline}
            title={decision.detail}
            wrap
            testid="decision-badge"
          />
          <div className="text-[10px] mt-1 leading-snug" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="decision-detail">
            {decision.detail}
          </div>
        </div>
      </div>

      <div
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-3 gap-y-2.5 mt-3 pt-3 border-t"
        style={{ borderColor: "var(--hoip-divider)" }}
      >
        <Cell label="Last">
          <div className="flex items-baseline gap-1.5 flex-wrap">
            <span className="text-[15px] font-mono-data">{t.price != null ? Number(t.price).toFixed(2) : "—"}</span>
            <DeltaText value={t.changePct} suffix="%" />
          </div>
          {t.live && t.changeLabel ? (
            <div className="text-[9px] mt-0.5 leading-snug" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="header-change-label">
              {t.changeLabel}
            </div>
          ) : null}
          {t.live && t.changeQualityLabel ? (
            <div className="text-[9px] leading-snug" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="header-change-quality">
              {t.changeQualityLabel}
            </div>
          ) : null}
          <div className="mt-1"><StatusBadge tier={q.tier} text={q.text} /></div>
        </Cell>

        <Cell label="HMM regime">
          <div className="text-[12px] font-mono-data truncate">{instantState || "—"}</div>
          <div className="text-[9px] mt-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>
            {t.regime.confidence != null ? `${Math.round(t.regime.confidence * 100)}% confidence` : "confidence unavailable"}
          </div>
        </Cell>

        <Cell label="Confirmed (5d)">
          <div className="text-[12px] font-mono-data truncate">{confirmedState || "—"}</div>
          {!canCompare ? (
            <div className="text-[9px] mt-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>comparison unavailable</div>
          ) : confirmedDiffers ? (
            <div className="mt-0.5"><StatusBadge tier="caution" text="Differs from instant" /></div>
          ) : (
            <div className="text-[9px] mt-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>Matches instant</div>
          )}
        </Cell>

        <Cell label="Technical score">
          <ScoreBar value={t.technicalScore} />
        </Cell>

        <Cell label="Fundamental score">
          <ScoreBar value={t.fundamentalScore} />
        </Cell>

        <Cell label={t.live && t.earnings?.past ? "Most recent earnings" : "Next earnings"}>
          {t.live && t.earnings?.past ? (
            <div data-testid="header-earnings-past">
              <div className="text-[12px] font-mono-data">
                Most recent earnings date: {t.earnings.date || "Date unavailable"}
              </div>
              <div className="text-[9px] mt-0.5 leading-snug" style={{ color: "var(--hoip-text-tertiary)" }}>
                Past date supplied — next event unavailable
              </div>
            </div>
          ) : (
            <>
              <div className="text-[12px] font-mono-data">
                {t.earnings.date || "Date unavailable"}
                {t.earnings.daysTo != null ? (
                  <span style={{ color: "var(--hoip-text-tertiary)" }}> · {t.earnings.daysTo}d</span>
                ) : null}
              </div>
              <div className="mt-0.5"><EarningsQualityBadge quality={t.earnings.quality} /></div>
            </>
          )}
        </Cell>
      </div>

      <div
        className="grid grid-cols-1 sm:grid-cols-3 gap-x-3 gap-y-1.5 mt-2 pt-2 border-t"
        style={{ borderColor: "var(--hoip-divider)" }}
        data-testid="header-decision-layers"
      >
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>
            A · Underlying / regime
          </div>
          <div className="text-[11px] mt-0.5">
            {t.regime.confirmedState}
            <span style={{ color: "var(--hoip-text-tertiary)" }}>
              {" "}· tech {t.technicalScore != null ? Number(t.technicalScore).toFixed(1) : "—"} · fund {t.fundamentalScore != null ? Number(t.fundamentalScore).toFixed(1) : "—"}
            </span>
          </div>
        </div>
        <div className="min-w-0" data-testid="header-premium-environment">
          <div className="text-[9px] uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>
            B · Premium environment
          </div>
          <div className="text-[11px] mt-0.5">{t.premium?.label || "IV context unavailable"}</div>
        </div>
        <div className="min-w-0" data-testid="header-strategy-readiness">
          <div className="text-[9px] uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>
            {t.live ? "C · Current product action" : "C · Selected-structure status"}
          </div>
          <div className="text-[11px] mt-0.5">
            {t.live ? (
              <>
                {LIVE_UNAVAILABLE.currentActionUnavailable}
                <span style={{ color: "var(--hoip-text-tertiary)" }}> · {LIVE_UNAVAILABLE.currentActionDetail}</span>
              </>
            ) : (
              <>
                {decision.headline}
                <span style={{ color: "var(--hoip-text-tertiary)" }}> · {decision.detail}</span>
              </>
            )}
          </div>
        </div>
      </div>

      <div
        className="flex items-center gap-2 flex-wrap mt-2.5 pt-2.5 border-t"
        style={{ borderColor: "var(--hoip-divider)" }}
      >
        <span className="text-[9px] uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>
          Options data
        </span>
        <OptionStateBadge state={t.optionsState} testid="option-state-badge" />
        <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
          {t.live
            ? (isAdmin
              ? "Chain state describes required quote fields and timestamp quality. Current product action stays unavailable without a verified option quote time."
              : "Option quote time is unverified.")
            : (isAdmin
              ? "Chain state describes required quote fields and timestamp quality. Basket action stays unavailable when those fields are missing."
              : "Data may be delayed.")}
        </span>
      </div>
    </header>
  );
}
